import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import RELoader from "../../components/common/RELoader";
import bikeDiscountService from "../../services/bikeDiscountService";
import { showToast } from "../../components/common/toastNotification";
import {
  BikeDiscountApprovalActionRequest,
  BikeDiscountApprovalDetailsData,
} from "../../types/bikeDiscount";

const Section: React.FC<{
  title: string;
  rows: Array<{ label: string; value: string }>;
}> = ({ title, rows }) => {
  return (
    <Box
      sx={{
        backgroundColor: "#4A4A4A",
        border: "1px solid #808080",
        p: 2,
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff", mb: 1.5 }}>
        {title}
      </Typography>
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "#B3B3B3", flexShrink: 0 }}>
            {row.label}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#FFFFFF",
              fontWeight: 600,
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {row.value || "-"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const BikeDiscountApprovalLinkPage: React.FC = () => {
  const location = useLocation();
  const token = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("token") || "";
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<BikeDiscountApprovalDetailsData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showDetailsModal, setShowDetailsModal] = useState(true);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [rejectRemarksError, setRejectRemarksError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<"Approved" | "Rejected" | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!token) {
      setLoadError("Invalid approval link.");
      showToast("Invalid approval link.", "error");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const response = await bikeDiscountService.getBikeDiscountApprovalDetails(token);
      const responseData = response?.data?.data ?? response?.data;
      if (response?.data?.success && responseData) {
        setDetails(responseData as BikeDiscountApprovalDetailsData);
      } else {
        const message =
          response?.data?.message || "Approval details not found.";
        setLoadError(message);
        // API can return success:false with HTTP 200; show toaster too.
        showToast(message, "error");
      }
    } catch (e) {
      setLoadError("Failed to load approval details.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleApprove = useCallback(async () => {
    if (!token) return;
    setActionLoading("Approved");
    try {
      const payload: BikeDiscountApprovalActionRequest = {
        token,
        action: "Approved",
        remarks: "",
      };
      const response = await bikeDiscountService.submitBikeDiscountApprovalAction(payload);
      if (response?.data?.success) {
        showToast("Bike Discount Approved", "success");
        setShowDetailsModal(false);
      } else {
        showToast(response?.data?.message || "Approval failed.", "error");
      }
    } catch {
      showToast("Approval failed.", "error");
    } finally {
      setActionLoading(null);
    }
  }, [token]);

  const handleRejectSubmit = useCallback(async () => {
    if (!token) return;
    const trimmed = rejectRemarks.trim();
    if (!trimmed) {
      setRejectRemarksError("Remarks are required.");
      return;
    }

    setRejectRemarksError(null);
    setActionLoading("Rejected");
    try {
      const payload: BikeDiscountApprovalActionRequest = {
        token,
        action: "Rejected",
        remarks: trimmed,
      };
      const response = await bikeDiscountService.submitBikeDiscountApprovalAction(payload);
      if (response?.data?.success) {
        showToast("Application Rejected", "error");
        setRejectModalOpen(false);
        setShowDetailsModal(false);
      } else {
        showToast(response?.data?.message || "Rejection failed.", "error");
      }
    } catch {
      showToast("Rejection failed.", "error");
    } finally {
      setActionLoading(null);
    }
  }, [rejectRemarks, token]);

  const personalRows = details
    ? [
        { label: "Name", value: details.employeeName },
        { label: "Mobile", value: details.phoneNumber },
        { label: "Email", value: details.email },
        { label: "Employee Code", value: details.employeeId },
        { label: "Department", value: details.department },
      ]
    : [];

  const bikeRows = details
    ? [
        { label: "Dealer Name", value: details.dealerName },
        { label: "Dealer", value: details.dealerCode },
        { label: "Model Code", value: details.modelName },
        { label: "Enquiry Category", value: details.approvalRemark },
      ]
    : [];

  const friendlyError = useMemo(() => {
    const msg = (loadError || "").toLowerCase();
    if (!msg) return { title: "Error", body: "" };

    if (msg.includes("already") && (msg.includes("reject") || msg.includes("action"))) {
      return {
        title: "Approval already actioned",
        body: loadError || "",
      };
    }

    if (msg.includes("not found") || msg.includes("application not found")) {
      return {
        title: "Application not found",
        body: loadError || "",
      };
    }

    return {
      title: "Unable to open this approval link",
      body: loadError || "",
    };
  }, [loadError]);

  return (
    <Box>
      {loading ? (
        <Box sx={{ p: 3 }}>
          <RELoader minHeight={260} />
        </Box>
      ) : loadError ? (
        <Box
          sx={{
            minHeight: 420,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{ color: "#fff", fontWeight: 800, fontSize: "16px" }}
          >
            {friendlyError.title}
          </Typography>
          <Typography sx={{ color: "#CCCCCC", maxWidth: 520 }}>
            {friendlyError.body}
          </Typography>
        </Box>
      ) : (
        <>
          <CustomModal
            open={showDetailsModal}
            handleClose={() => setShowDetailsModal(false)}
            title="Bike Discount Application Details"
            onSave={() => undefined}
            isSaveButtonShow={false}
            width={520}
            borderBottom="0px"
          >
            {details && (
              <Box>
                <Section title="Personal Information" rows={personalRows} />
                <Section title="Bike Information" rows={bikeRows} />

                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
                  <Button
                    onClick={handleApprove}
                    disabled={actionLoading !== null}
                    variant="contained"
                    sx={{
                      backgroundColor: "#393939",
                      borderRadius: 0,
                      border: "1px solid #FFFFFF",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      textTransform: "none",
                      width: 150,
                      height: 40,
                      "&:hover": { backgroundColor: "#FFFFFF", color: "#da291c" },
                    }}
                  >
                    Accept
                  </Button>

                  <Button
                    onClick={() => setRejectModalOpen(true)}
                    disabled={actionLoading !== null}
                    variant="contained"
                    sx={{
                      backgroundColor: "#393939",
                      borderRadius: 0,
                      border: "1px solid #FFFFFF",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      textTransform: "none",
                      width: 150,
                      height: 40,
                      "&:hover": { backgroundColor: "#FFFFFF", color: "#da291c" },
                    }}
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            )}
          </CustomModal>

          <CustomModal
            open={rejectModalOpen}
            handleClose={() => setRejectModalOpen(false)}
            title="Rejection Remarks"
            subtitle="Please provide a reason for rejecting this application"
            onSave={handleRejectSubmit}
            saveButtonName="Submit"
            width={520}
            btnLoading={actionLoading === "Rejected"}
            borderBottom="0px"
          >
            <CustomInput
              label="Remarks"
              type="text"
              name="rejectionRemarks"
              placeholder="Enter rejection remarks..."
              value={rejectRemarks}
              onChange={(e) => {
                setRejectRemarks(e.target.value);
                setRejectRemarksError(null);
              }}
              backgroundColor="#393939"
              error={rejectRemarksError || undefined}
              rows={4}
            />
          </CustomModal>
        </>
      )}
    </Box>
  );
};

export default BikeDiscountApprovalLinkPage;

